/**
 * Uses contentful Content Management api
 * @see https://www.contentful.com/developers/docs/references/content-management-api/
 */

import { createClient } from 'contentful-management';

import { appServices } from '../helpers/app-services/AppServices/index';
import config from '../../config/config';

const spaceId = config.CONTENTFUL_SPACE_ID;
const envId = config.CONTENTFUL_ENVIRONMENT;

let CONTENTFUL_ENVIRONMENT = null;
export const getContentfulEnvironment = async accessToken => {
  if (CONTENTFUL_ENVIRONMENT) return;
  const client = createClient({ accessToken });
  const space = await client.getSpace(spaceId);
  CONTENTFUL_ENVIRONMENT = await space.getEnvironment(envId);
};

const CONTENTFUL_LOCALE = 'en-US';

const addEntry = async (scribeData, title, canvasSize, thumbnailBuffer) => {
  const environment = CONTENTFUL_ENVIRONMENT;
  if (!environment) {
    throw new Error('Contentful authorization not set up. Try Authenticate with Contentful link on homepage.');
  }
  try {
    const thumbnailAsset = await environment.createAssetFromFiles({
      fields: {
        title: {
          [CONTENTFUL_LOCALE]: `${title} template thumbnail`
        },
        file: {
          [CONTENTFUL_LOCALE]: {
            contentType: 'image/png',
            fileName: 'thumbnail.png',
            file: thumbnailBuffer
          }
        }
      }
    });
    const processedAsset = await thumbnailAsset.processForAllLocales();
    await processedAsset.publish();

    const entry = await environment.createEntry('scribeTemplate', {
      fields: {
        title: {
          [CONTENTFUL_LOCALE]: title
        },
        canvasSize: {
          [CONTENTFUL_LOCALE]: canvasSize
        },
        scribeData: {
          [CONTENTFUL_LOCALE]: scribeData
        },
        templateThumbnail: {
          [CONTENTFUL_LOCALE]: {
            sys: {
              type: 'Link',
              linkType: 'Asset',
              id: processedAsset.sys.id
            }
          }
        }
      }
    });
    return entry;
  } catch (error) {
    throw error;
  }
};

const ADD_ASSETS_CACHE = {};
const addAsset = async ({ title, fileName, contentType, fromUrl }) => {
  if (title in ADD_ASSETS_CACHE) return ADD_ASSETS_CACHE[title];
  try {
    const environment = CONTENTFUL_ENVIRONMENT;
    const fileBuffer = await (await window.fetch(fromUrl)).arrayBuffer();
    const asset = await environment.createAssetFromFiles({
      fields: {
        title: {
          [CONTENTFUL_LOCALE]: title
        },
        file: {
          [CONTENTFUL_LOCALE]: {
            contentType,
            fileName,
            file: fileBuffer
          }
        }
      }
    });
    const processedAsset = await asset.processForAllLocales();
    const publishedAsset = await processedAsset.publish();
    ADD_ASSETS_CACHE[title] = publishedAsset;
    return publishedAsset;
  } catch (error) {
    throw error;
  }
};

/**
 * Saves an image from a Scribe into contentful
 * Removes the url and adds the contentful id
 */
export const saveImage = async image => {
  return addAsset({
    title: `Scribely template image ${image.image.assetId}`,
    fileName: `scr-${image.image.assetId}.${image.image.contentType.substr(6)}`,
    contentType: image.image.contentType,
    fromUrl: await appServices.getAssetUrl(image.image.assetId)
  }).then(asset => ({ contentfulAsset: asset, originalImage: image }));
};

export const saveTemplateAudio = async audioClip => {
  return addAsset({
    title: `Scribely template audio ${audioClip.assetId}`,
    fileName: `scr-${audioClip.assetId}.mp3`,
    contentType: 'audio/mpeg',
    fromUrl: await appServices.getAssetUrl(audioClip.assetId)
  });
};

export const saveTemplate = (scribeData, thumbnailBuffer) => {
  const { title, canvasSize } = scribeData;
  return addEntry(scribeData, title, canvasSize, thumbnailBuffer);
};
